import * as moment from "moment";
import * as React from "react";
import AutomationUtils from "sh-application/utility/AutomationUtil";
import DateTimeFormatter from "sh-application/utility/DateTimeFormatter";
import DateUtils from "sh-application/utility/DateUtils";
import StartOfWeekPicker from "./StartOfWeekPicker";
import StringsStore from "sh-strings/store";
import { DatePicker, DayOfWeek, IDatePickerStrings, IDatePicker, ICalloutProps } from "@fluentui/react";
import { Moment } from "moment";

const styles = require("./LocalizedDatePicker.scss");
const classNames = require("classnames/bind");

interface LocalizedDatePickerProps {
    title?: string;
    datePickerClassName?: string;
    value: moment.Moment;
    /** @deprecated All callers pass the team timezone (this.props.timeZoneOlsonCode). Remove this prop and read the timezone directly in Phase 7. */
    timeZoneOlsonCode: string;
    isMonthPickerVisible?: boolean;
    showStartDayOfWeek?: boolean;
    onDateSelectedCallBack:  (date: moment.Moment) => void;
    startDayOfWeek?: DayOfWeek;
    onStartDayOfWeekChanged?: (dayOfWeekIndex: number) => void;
    ariaLabelDate: string;
    ariaLabeledBy?: string;
    ariaLabelStartOfWeek?: string;
    disableAutoFocus?: boolean;
    isRequired?: boolean;
    disabled?: boolean;
    calloutProps?: ICalloutProps;
}

export default class LocalizedDatePicker extends React.Component<LocalizedDatePickerProps, any> {
    private _strings: Map<string, string>;
    private _dayPickerStrings: IDatePickerStrings;
    private _isEmpty: boolean;
    private _datepicker = React.createRef<IDatePicker>();

    public constructor(props: LocalizedDatePickerProps) {
        super(props);
        this._datepicker = React.createRef<IDatePicker>();

        this._strings = StringsStore().registeredStringModules.get("teamsettings").strings;

        this._dayPickerStrings = {
            months: [], shortMonths: [], days: [], shortDays: [],
            goToToday: this._strings.get("goToToday"),
            prevMonthAriaLabel: this._strings.get("goToPreviousMonth"),
            nextMonthAriaLabel: this._strings.get("goToNextMonth"),
            prevYearAriaLabel: this._strings.get("goToPreviousYear"),
            nextYearAriaLabel: this._strings.get("goToNextYear"),
            invalidInputErrorMessage: this._strings.get("invalidDateErrorMessage")
        };

        this._dayPickerStrings.days = DateTimeFormatter.getWeekdayNames("long"); // e.g., ["Sunday", "Monday", ...]
        this._dayPickerStrings.months = DateTimeFormatter.getMonthNames("long"); // e.g., ["January", "February", ...]
        this._dayPickerStrings.shortMonths = DateTimeFormatter.getMonthNames("short"); // e.g., ["Jan", "Feb", ...]
        this._dayPickerStrings.shortDays = DateTimeFormatter.getWeekdayNames("short"); // e.g., ["Sun", "Mon", ...]
        this._dayPickerStrings.isRequiredErrorMessage = this._strings.get("invalidDateErrorMessage");
    }

    // this is executed onblur event for datepicker
    private onLostFocus = () => {
        if (this.props.isRequired && this._isEmpty) {
            // this reset is needed since onchenge do not update state value when the datepicker is empty
            // but datepickers allow been empty causing a disconnection of the datepicker and its value
            // ex. datepicker is empty but state value has the last value that was choose on datepicker
            // with this event everytime that dataepicker lost function if datepicker is empty will force
            // the component to return to state value.
            this._datepicker.current.reset();
            this.onDateChanged(this.props.value.toDate());
        }
    }

    public render() {

        // The DatePicker component uses native Javascript Date, so we need to convert our date time values, which are in the specified timezone,
        // to display versions for the user's browser.  Any date values that are returned from the component need to be converted back to the
        // timezone.
        let displayDateValue: Date = this.props.value ? DateUtils.convertTimezoneDateTimeToDisplayTime(this.props.value.toDate()) : null;

        return (
            <div className={ classNames(styles.datePickerContainer, AutomationUtils.getAutomationId("components", "QAIDLocalizedDatePicker")) }>
                <DatePicker
                    disabled={ this.props.disabled }
                    componentRef = { this._datepicker }
                    className={ this.props.datePickerClassName ? this.props.datePickerClassName : "" }
                    allowTextInput={ true }
                    label={ this.props.title ? this.props.title : ""  }
                    ariaLabel={ this.props.ariaLabelDate }
                    aria-labelledby={ this.props.ariaLabeledBy}
                    value={ displayDateValue }
                    strings={ this._dayPickerStrings }
                    onSelectDate={ this.onDateChanged }
                    placeholder={ DateTimeFormatter.formatBrowserDate(DateUtils.convertTimezoneDateTimeToDisplayTime(new Date())) /* e.g., "4/14/2026" */ }
                    parseDateFromString={ (dateStr: string) => this.parseDateFromString(dateStr) }
                    formatDate={ this.formatDate }
                    isMonthPickerVisible={ false }
                    disableAutoFocus={ !!this.props.disableAutoFocus }
                    firstDayOfWeek={ this.props.startDayOfWeek }
                    calloutProps={ this.props.calloutProps}
                    onBlur = { this.onLostFocus }>
                </DatePicker>
                {
                    this.props.showStartDayOfWeek && this.props.showStartDayOfWeek === true &&
                    <div className={ styles.startDayOfWeekBox }>
                        <div className={ styles.datePickerLabel }>
                            { this._strings.get("startOfWeek") }
                        </div>
                        <div>
                            <StartOfWeekPicker
                                disabled={ this.props.disabled }
                                startDayOfWeek={ this.props.startDayOfWeek }
                                className={ styles.startOfWeekDropDown }
                                ariaLabelStartOfWeek={ this.props.ariaLabelStartOfWeek }
                                onChanged={ this.onStartOfWeekChanged } />
                        </div>
                    </div>
                }
            </div>
        );
    }

    onDateChanged = (newDate: Date) => {
        this._isEmpty = !newDate;
        if (newDate && this.props.onDateSelectedCallBack) {
            newDate.setHours(0, 0, 0, 0); // GoToToday can select the Now value. Clear the time

            // The DatePicker component uses native Javascript Date, so we need to convert our date time values, which are in the specified timezone,
            // to display versions for the user's browser.  Any date values that are returned from the component need to be converted back to the
            // timezone.
            const dateSelectedInTimeZone = DateUtils.convertDisplayTimeToTimezoneDateTime(newDate);
            this.props.onDateSelectedCallBack(moment(dateSelectedInTimeZone).tz(this.props.timeZoneOlsonCode));
        }
    }

    onStartOfWeekChanged = (index: number) => {
        if (this.props.onStartDayOfWeekChanged) {
            this.props.onStartDayOfWeekChanged(index);
        }
    }

    formatDate = (date: Date): string => {
        if (!date) {
            return "";
        }
        // The display Date's local components already represent the correct date in the picker's timezone.
        // Format those components directly without further timezone conversion.
        return DateTimeFormatter.formatBrowserDate(date); // e.g., "4/14/2026"
    }

    /**
     * Parses the display date string in the current format
     * @param dateAsString Display string of the date
     * @returns {Date}
     */
    parseDateFromString(dateAsString: string): Date {
        // When an invalid date value is entered manually, return the previously selected date value here.
        // The DatePicker expects a "display" Date whose local components show the date in the picker's timezone.
        if (dateAsString && dateAsString.length > 0) {
            const parsedDate = DateTimeFormatter.parseBrowserDate(dateAsString);
            if (parsedDate) {
                // Returns a Date with browser-local components matching what the user typed.
                return parsedDate;
            }
        }
        // Invalid or empty input: fall back to the previously selected display date
        const fallbackDate = this.props.value ? this.props.value.toDate() : new Date();
        return DateUtils.convertTimezoneDateTimeToDisplayTime(fallbackDate);
    }
}